import { html } from "core/Controls/Books/BookBrowser.html";
import { type CollectionView, View, type ViewData } from "silver";
import { type BookControlService } from "core/Services/BookControlService";
import { type WindowLayout } from "core/WindowLayout";
import { type BookDataService } from "core/Services/BookDataService";
import { type BookControl, BookControlViewMode } from "core/Controls/Books/BookControl";
import { type BooksLang } from "Lang";

interface BookViewData {
	element: HTMLElement;
}

interface BookBrowserViewData extends ViewData {
	lang: BooksLang;
	books: number[];
	viewAsList: boolean;
	booksPerRow: number;
}

interface BookBrowserView extends View<BookBrowserViewData> {
	loading: HTMLDivElement;
	books: CollectionView<BookViewData>;
}

export class BookBrowser {

	private bookIds: number[] = [];
	private loadingBooks: number = 0;
	private loadedBooks: number = 0;
	private bookControls: BookControl[] = [];

	private view: BookBrowserView;

	private static readonly LOAD_SCROLL_BUFFER: number = 1000;
	private static readonly LOAD_BATCH_ROWS: number = 5;
	private readonly layoutListener: () => any;
	private bookIdToProject: Record<string, number> = { };

	constructor(
		private bookControlService: BookControlService,
		private bookDataService: BookDataService,
		private windowLayout: WindowLayout,
		lang: BooksLang
	) {
		this.view = new View<BookBrowserViewData>(html, { }, { lang, books: [], viewAsList: false, booksPerRow: 6 }) as BookBrowserView;
		this.layoutListener = () => this.autoLoad().catch(console.error);
		this.listenToLayoutEvents();
	}

	public set booksPerRow(value: number) {
		this.view.dataset.booksPerRow = value;
	}

	public get booksPerRow(): number {
		return this.view.dataset.booksPerRow;
	}

	public listenToLayoutEvents(subscribe: boolean = true): void {
		if (subscribe) {
			this.windowLayout.onScroll(this.layoutListener);
			this.windowLayout.onUpdate(this.layoutListener);
		} else {
			this.windowLayout.onScroll.unsubscribe(this.layoutListener);
			this.windowLayout.onUpdate.unsubscribe(this.layoutListener);
		}
	}

	public clearBooks(): void {
		this.view.books.empty();
		this.bookControls = [];
		this.bookIds = [];
		this.loadingBooks = 0;
		this.loadedBooks = 0;
		this.view.element.classList.remove("-loading");
	}

	public pushBookIds(bookIds: number[], bookIdToProject: Record<string, number> = { }): Promise<void> {
		Object.entries(bookIdToProject).forEach(([bookId, projectId]) => {
			this.bookIdToProject[bookId] = projectId;
		});

		for (let i = 0; i < bookIds.length; i++) {
			const bookId = Number(bookIds[ i ]);
			if (!this.bookIds.includes(bookId)) {
				this.bookIds.push(bookId);
			}
		}

		return this.autoLoad();
	}

	public async loadAll(): Promise<void> {
		return this.loadBooks(this.bookIds.length);
	}

	public async autoLoad(): Promise<void> {
		if (this.isLoading() || this.isFullyLoaded() || !this.isInDocument()) {
			return;
		}
		const elementBottom:  number = this.view.element.getBoundingClientRect().bottom;
		const viewportHeight: number = this.windowLayout.viewportHeight;

		if (elementBottom - viewportHeight >= BookBrowser.LOAD_SCROLL_BUFFER) {
			return;
		}
		return this.loadBooks(BookBrowser.LOAD_BATCH_ROWS * this.booksPerRow);
	}

	private async loadBooks(n: number): Promise<void> {
		this.loadingBooks = Math.min(this.loadedBooks + n, this.bookIds.length);
		const booksToLoad = this.bookIds.slice(this.loadedBooks, this.loadingBooks);

		if (booksToLoad.length === 0) {
			return;
		}

		this.view.element.classList.add("-loading");
		const preloadBookPromise = this.bookDataService.getFromIds(booksToLoad).then(() => {
			this.view.element.classList.remove("-loading");
		});
		const viewMode = this.viewMode;
		booksToLoad.forEach((id: number) => {
			const projectId = this.bookIdToProject[id] || undefined;
			const control = this.bookControlService.makeBookControl(id, projectId);
			this.bookControls.push(control);
			control.setViewMode(viewMode);

			this.view.books.add(String(id), { element: control.element });
		});

		await preloadBookPromise;
		this.loadedBooks = this.loadingBooks;
		this.autoLoad();
	}

	private isLoading(): boolean {
		return (this.loadingBooks !== this.loadedBooks);
	}

	private isInDocument(): boolean {
		return this.view.element.offsetHeight !== 0;
	}

	private isFullyLoaded(): boolean {
		return this.loadedBooks === this.bookIds.length;
	}

	public get element(): HTMLElement {
		return this.view.element;
	}

	public get viewMode(): BookControlViewMode {
		return this.view.dataset.viewAsList ? BookControlViewMode.ROW : BookControlViewMode.CARD;
	}

	public set viewMode(mode: BookControlViewMode) {
		if (this.viewMode != mode) {
			this.view.dataset.viewAsList = mode == BookControlViewMode.ROW;
			this.bookControls.forEach(control => control.setViewMode(mode));
		}
	}
}
